import type { AnnotationEntry, AnnotationFeature } from '@geosoft/viewer-sdk';

export interface ExportSnapshotInput {
  readonly visibleLayerIds: ReadonlyArray<string>;
  readonly time: Date | null;
  readonly annotations: ReadonlyArray<AnnotationEntry>;
  readonly measurePoints: ReadonlyArray<readonly [number, number]>;
  readonly exportedAt?: Date;
}

export function buildGeoJsonExport(annotations: ReadonlyArray<AnnotationEntry>) {
  return {
    type: 'FeatureCollection' as const,
    features: annotations.map(annotationToFeature),
  };
}

export function buildGprojAnnotationsSubset(annotations: ReadonlyArray<AnnotationEntry>) {
  return {
    annotations,
  };
}

export function buildSnapshotExport(input: ExportSnapshotInput) {
  return {
    exportedAt: (input.exportedAt ?? new Date()).toISOString(),
    visibleLayerIds: [...input.visibleLayerIds],
    time: input.time?.toISOString() ?? null,
    annotations: buildGprojAnnotationsSubset(input.annotations).annotations,
    measurePoints: input.measurePoints,
  };
}

export function buildKmlExport(annotations: ReadonlyArray<AnnotationEntry>): string {
  const placemarks = annotations
    .map((annotation) => {
      if (annotation.geometry.type !== 'Point') return '';
      const [lon, lat] = annotation.geometry.coordinates as [number, number];
      const label = escapeXml(String(annotation.properties.label ?? annotation.kind));
      return `<Placemark><name>${label}</name><Point><coordinates>${lon},${lat},0</coordinates></Point></Placemark>`;
    })
    .filter(Boolean)
    .join('');

  return `<?xml version="1.0" encoding="UTF-8"?><kml xmlns="http://www.opengis.net/kml/2.2"><Document>${placemarks}</Document></kml>`;
}

export function downloadTextFile(filename: string, contents: string, type: string): void {
  const blob = new Blob([contents], { type });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

function annotationToFeature(annotation: AnnotationEntry): AnnotationFeature {
  return {
    type: 'Feature',
    geometry: annotation.geometry,
    properties: {
      ...annotation.properties,
      id: annotation.id,
      kind: annotation.kind,
      createdBy: annotation.createdBy,
      createdAt: annotation.createdAt,
      updatedAt: annotation.updatedAt,
      style: annotation.style,
    },
  };
}

function escapeXml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}
